import { Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ErrorBoundary } from '../feedback/error-boundary.js';
import { ConnectionBanner } from '../offline/connection-banner.js';
import { TrialBanner } from '../billing/trial-banner.js';
import { cn } from '@tailoring-crm/ui';
import { useSession } from '../../lib/session/session-context.js';
import { TourProvider } from '../onboarding/tour/tour-controller.js';
import { AppHeader } from './app-header.js';
import { BottomNav } from './bottom-nav.js';
import { hasSectionColumn } from './nav-items.js';
import { Sidebar } from './sidebar.js';
import { useNavCollapsed } from './use-nav-collapsed.js';

/**
 * The chrome every screen renders inside.
 *
 * Two layouts, one component. Below `md` it is a single column with a fixed bottom navigation
 * bar; at `md` and up the navigation moves into a fixed sidebar and the content shifts across
 * to make room. Nothing about a feature screen changes between the two — they render into
 * `<Outlet />` and the shell decides the shape.
 *
 * The error boundary sits INSIDE the shell on purpose: a screen that throws leaves the
 * navigation and the connection banner intact, so the owner can walk away from the broken
 * screen instead of reloading the app.
 */
export function AppShell({ onSignOut }: { onSignOut?: () => void }) {
  const { t } = useTranslation();
  const nav = useNavCollapsed();
  const role = useSession()?.role;
  const { pathname } = useLocation();
  // A one-screen section has no column to show, whatever the remembered choice says; the
  // choice itself is left alone, so the next section with a column opens the way it was left.
  const folded = nav.collapsed || !hasSectionColumn(role, pathname);

  return (
    // The first-order spotlight tour rides on top of every screen, so it lives in the chrome.
    <TourProvider>
      <div className="min-h-dvh bg-background md:has-[[data-fills-viewport]]:h-dvh md:has-[[data-fills-viewport]]:overflow-hidden">
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-primary focus:px-4 focus:py-2 focus:text-primary-foreground"
        >
          {t('common.skipToContent')}
        </a>

        <AppHeader {...(onSignOut ? { onSignOut } : {})} />
        <Sidebar collapsed={folded} onToggle={nav.toggle} />

        {/*
        A screen may ask the shell to stop being a document and start being a window.
        It asks by carrying `data-fills-viewport`, and the shell answers here: the column stops
        growing with its content, the overflow is cut, and the screen inside is handed exactly
        the height that is left. What it does with that height is its own business — the customer
        book spends it on rows and scrolls them under a heading row
        that never moves.

        At `md` and up ONLY. Below it the page is read by scrolling and a screen pinned to the
        viewport would be a letterbox between two bands of chrome.

        Nothing has to be reset: the attribute lives on the screen's own element, so unmounting
        the screen — including the error boundary replacing it — takes the answer with it.
      */}
        <div
          // Folding the section column away hands its width to the screen: the content pads by
          // the rail alone and slides across with the sidebar.
          className={cn(
            'flex min-h-dvh flex-col pt-header motion-safe:duration-200 motion-safe:transition-[padding] md:has-[[data-fills-viewport]]:h-dvh md:has-[[data-fills-viewport]]:min-h-0',
            folded ? 'md:pl-rail' : 'md:pl-sidebar',
          )}
        >
          <ConnectionBanner />

          {/*
          The trial countdown (task 24), beside the connection banner because it is the same
          kind of thing: a fact about the shop's standing that belongs wherever the owner
          already is, rather than on a screen they have to remember to visit. It renders
          nothing at all until the trial is close enough to matter.
        */}
          <div className="px-4 pt-2 md:px-6">
            <TrialBanner />
          </div>

          <main
            id="main-content"
            // The bottom padding clears the mobile navigation bar (and the home indicator under
            // it) so the last row of a long list is always reachable.
            // `bg-canvas` and not `bg-background`: the page is grey so the white cards on it read
            // as surfaces, but every input and dialog in the app is `bg-background` and must stay
            // white. Only this one element carries the grey.
            // A viewport-filling screen turns this into a flex column so the screen can take the
            // height rather than overflow it, and trades the roomy bottom padding for the top's
            // — with nothing below the fold, that space is a margin, not a run-out.
            className="flex-1 bg-canvas px-4 pb-[calc(5rem+env(safe-area-inset-bottom))] pt-4 md:px-6 md:pb-8 md:has-[[data-fills-viewport]]:flex md:has-[[data-fills-viewport]]:min-h-0 md:has-[[data-fills-viewport]]:flex-col md:has-[[data-fills-viewport]]:pb-4"
          >
            <ErrorBoundary>
              <Outlet />
            </ErrorBoundary>
          </main>
        </div>

        <BottomNav />
      </div>
    </TourProvider>
  );
}
